import { css } from '@emotion/core';
import {
    Button,
    Chip,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Theme,
    Tooltip,
    Typography,
    useTheme
} from '@material-ui/core';
import { ArrowDownward, ArrowUpward, Cancel, CheckCircle, HelpOutline } from '@material-ui/icons';
import React from 'react';

import { getDisqualReasonLabel } from 'shared/models/job-stages';
import { ScoringBriefResultPreview } from 'shared/models/profile-score';

import { fullDateTime } from '../common/timestamp';
import { useSlides } from '../hooks/use-candidate-slides';
import { useProfileAdornments } from '../hooks/use-profile-adornment';
import { DecisionScore } from './decision-score';

interface JobScoringBriefPreviewDialogProps {
    open: boolean;
    rows: ScoringBriefResultPreview[];
    onClose: () => void;
}

const tableColumnCount = 4;

const styles = (theme: Theme) => css`
    .MuiDialogContent-root {
        min-width: 760px;
    }

    .MuiTableCell-root {
        vertical-align: top;
    }

    .candidate-link {
        color: ${theme.palette.primary.main};
        text-decoration: none;
        cursor: pointer;
    }

    .empty-state {
        color: ${theme.palette.text.secondary};
    }

    .row-green {
        background-color: ${theme.palette.success.main}22;
    }

    .row-red {
        background-color: ${theme.palette.error.main}22;
    }

    .row-yellow {
        background-color: ${theme.palette.warning.main}22;
    }

    .reason-text {
        max-width: 300px;
    }
`;

const adornmentStyles = css`
    display: flex;
    gap: 8px;
    align-items: center;
    padding: 4px 0;
`;

type MatchType = 'correct' | 'wrong' | 'maybe';

function isRecruiterDeclined(row: ScoringBriefResultPreview): boolean {
    return row.disqualified && row.stage === 'response_received';
}

function getMatchType(row: ScoringBriefResultPreview): MatchType {
    const { decision } = row;
    if (!decision || decision === 'MAYBE') return 'maybe';
    const declined = isRecruiterDeclined(row);
    if (decision === 'REACH_OUT') {
        return declined ? 'wrong' : 'correct';
    }
    return declined ? 'correct' : 'wrong';
}

const matchColors: Record<MatchType, { bg: string; border: string }> = {
    correct: { bg: '#388e3c22', border: '#388e3c' },
    maybe: { bg: '#f57c0022', border: '#f57c00' },
    wrong: { bg: '#d32f2f22', border: '#d32f2f' }
};

const matchIcons: Record<MatchType, React.ReactElement> = {
    correct: <CheckCircle fontSize="small" style={{ color: '#388e3c' }} />,
    maybe: <HelpOutline fontSize="small" style={{ color: '#f57c00' }} />,
    wrong: <Cancel fontSize="small" style={{ color: '#d32f2f' }} />
};

function getOutcomeDisplay(row: ScoringBriefResultPreview) {
    const match = getMatchType(row);
    const chipColors = matchColors[match];
    const chipIcon = matchIcons[match];
    if (row.disqualified && row.disqualReason && isRecruiterDeclined(row)) {
        const reason = getDisqualReasonLabel(row.disqualReason as any);
        return { chipColors, chipIcon, label: `Recruiter DQ - ${reason}`, tooltip: reason };
    }
    const stage = row.stageLabel || row.stage;
    if (row.disqualified) {
        const reason = row.disqualReason ? getDisqualReasonLabel(row.disqualReason as any) : 'Disqualified';
        return { chipColors, chipIcon, label: `${stage}, DQ`, tooltip: reason };
    }
    return {
        chipColors,
        chipIcon,
        label: stage,
        tooltip: row.lastStageChangedAt ? fullDateTime(row.lastStageChangedAt) : 'Active'
    };
}

function getRowClass(row: ScoringBriefResultPreview): string {
    const match = getMatchType(row);
    return match === 'correct' ? 'row-green' : match === 'wrong' ? 'row-red' : 'row-yellow';
}

function truncateWords(text: string, maxWords: number): string {
    const words = text.split(/\s+/);
    if (words.length <= maxWords) return text;
    return words.slice(0, maxWords).join(' ') + '…';
}

const maxReasonWords = 100;

const ReasonWithTooltip: React.FC<{ reason: string | null }> = ({ reason }) => {
    if (!reason) return <span>-</span>;
    const truncated = truncateWords(reason, maxReasonWords);
    if (truncated === reason) return <span>{reason}</span>;
    return (
        <Tooltip title={reason} placement="top-start">
            <span>{truncated}</span>
        </Tooltip>
    );
};

const OutcomeCell: React.FC<{ row: ScoringBriefResultPreview }> = ({ row }) => {
    const { label, tooltip } = getOutcomeDisplay(row);
    const icon = row.disqualified ? (
        <Tooltip title={tooltip}>
            <ArrowDownward fontSize="small" style={{ verticalAlign: 'middle', marginLeft: 4, color: '#d32f2f' }} />
        </Tooltip>
    ) : (
        <Tooltip title={tooltip}>
            <ArrowUpward fontSize="small" style={{ verticalAlign: 'middle', marginLeft: 4, color: '#388e3c' }} />
        </Tooltip>
    );
    return (
        <span>
            {label}
            {icon}
        </span>
    );
};

export const JobScoringBriefPreviewDialog: React.FC<JobScoringBriefPreviewDialogProps> = ({ open, rows, onClose }) => {
    const theme = useTheme();
    const { setList } = useSlides();
    const { pushAdornmentGenerator } = useProfileAdornments();

    React.useEffect(() => {
        const getAdornments = (personId: string) => {
            const row = rows.find((r) => r.personId === personId);
            if (!row?.decision) return {};
            const { chipColors, chipIcon, label, tooltip } = getOutcomeDisplay(row);
            const outcomeChipStyles = css`
                background-color: white !important;
                color: ${chipColors.border} !important;
            `;
            return {
                insertElements: [
                    {
                        element: (
                            <div css={adornmentStyles}>
                                <Tooltip title={tooltip}>
                                    <Chip css={outcomeChipStyles} icon={chipIcon} label={label} variant="outlined" />
                                </Tooltip>
                                <DecisionScore decision={row.decision} tooltip={row.reason ?? undefined} />
                            </div>
                        ),
                        slot: 'scoreDetailedView' as const
                    }
                ]
            };
        };
        return pushAdornmentGenerator(getAdornments);
    }, [rows, pushAdornmentGenerator]);

    const handleCandidateClick = (personId: string) => () => {
        const list = rows.map((row) => ({ personId: row.personId }));
        setList(list, { personId });
    };

    const tableRows = rows.length ? (
        rows.map((row, index) => (
            <TableRow key={`${row.personId}-${index}`} className={getRowClass(row)}>
                <TableCell onClick={handleCandidateClick(row.personId)} className="candidate-link">
                    {row.candidateName}
                </TableCell>
                <TableCell>
                    <OutcomeCell row={row} />
                </TableCell>
                <TableCell>{row.decision ? <DecisionScore decision={row.decision} /> : '-'}</TableCell>
                <TableCell className="reason-text">
                    <ReasonWithTooltip reason={row.reason} />
                </TableCell>
            </TableRow>
        ))
    ) : (
        <TableRow>
            <TableCell className="empty-state" colSpan={tableColumnCount}>
                No recent Recruiter declined or client accepted candidates found
            </TableCell>
        </TableRow>
    );

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="lg"
            fullWidth={true}
            css={styles(theme)}
            disableEnforceFocus={true}
        >
            <DialogTitle>
                <Typography variant="h4" component="div">
                    Scoring Results Preview
                </Typography>
            </DialogTitle>
            <DialogContent>
                <Table>
                    <TableHead>
                        <TableRow>
                            <TableCell>Candidate</TableCell>
                            <TableCell>Outcome</TableCell>
                            <TableCell>Decision</TableCell>
                            <TableCell>Reason</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>{tableRows}</TableBody>
                </Table>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>Close</Button>
            </DialogActions>
        </Dialog>
    );
};
